<script setup lang="ts">
import type { PageTileFragment } from '#graphql-operations'

defineProps<{
  page: PageTileFragment
  lazy: boolean
}>()
</script>

<template>
  <article
    v-if="page"
    :class="`cms-tile cms-tile--${page?.Type?.toLocaleLowerCase()}`"
  >
    <div class="cms-tile__wrapper">
      <div class="cms-tile__content">
        <time
          v-if="page?.Type === 'BlogDetail' && page?.PublicationDate"
          :datetime="page.PublicationDate"
          class="cms-tile__date"
        >
          {{
            $td(new Date(page.PublicationDate), {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          }}
        </time>

        <UiHeading
          class="cms-tile__heading"
        >
          <AppLink
            class="cms-tile__link"
            :to="`/${page?.PageUrl?.url}`"
          >
            {{ page?.PageTitle }}
          </AppLink>
        </UiHeading>

        <p
          class="cms-tile__description"
        >
          {{ page?.Description }}
        </p>

        <div
          aria-hidden="true"
          class="cms-tile__button"
        >
          <span>
            {{ page.ButtonText || $t('Read more') }}
          </span>
        </div>
      </div>

      <div class="cms-tile__visual">
        <picture v-if="page?.Image?.url">
          <source
            :srcset="useXcdnImage(page.Image.url, 'f=rs:fit:436')"
            media="(min-width: 1024px)"
          >
          <source
            :srcset="useXcdnImage(page.Image.url, 'f=rs:fit:436')"
            media="(min-width: 768px)"
          >
          <source
            :srcset="useXcdnImage(page.Image.url, 'f=rs:fit:436')"
          >
          <img
            v-if="page?.Image?.url"
            class="cms-tile__image"
            :src="useXcdnImage(page.Image.url, 'f=rs:fit:436')"
            :alt="page.Image.alternativeText || page.PageTitle"
            :width="page.Image.width"
            :height="page.Image.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'low' : 'high'"
            :decoding="lazy ? 'async' : 'sync'"
          >
        </picture>

        <div
          v-if="page?.Type === 'BlogDetail' && page?.Tags?.length"
          class="cms-tile__labels"
        >
          <span
            v-for="tag in page.Tags"
            :key="tag.Tag"
            class="cms-tile__label"
          >
            {{ tag.Tag }}
          </span>
        </div>

        <StrapiSticker
          v-if="page?.Sticker"
          :sticker="page.Sticker"
          class="cms-tile__sticker"
        />
      </div>
    </div>
  </article>
</template>

<style>
@import url("~layers/components/Cms/Tile.css");
</style>
